import { useCallback, useState, type FormEvent } from "react";

import type { Papel } from "@ghm/domain";

import { useAuth } from "@/features/auth/AuthContext";
import { ErrorState } from "@/features/devices/components/ErrorState";
import { LoadingState } from "@/features/devices/components/LoadingState";
import { ApiError } from "@/lib/http";
import { hasLoadedOnce, useAsync } from "@/lib/useAsync";

import { cadastrarMembro, enviarPainel, listarMembros, removerMembro, type Membro } from "./api";

/** Mensagem da API quando ela explica a recusa; senão, a do erro. */
function mensagemDe(falha: unknown, fallback: string): string {
  if (falha instanceof ApiError && typeof falha.body === "object" && falha.body) {
    return String((falha.body as { message?: unknown }).message ?? falha.message);
  }
  return falha instanceof Error ? falha.message : fallback;
}

/**
 * Quem tem acesso à planta, e o cadastro de gente nova.
 *
 * É uma SEÇÃO, não uma página: mora dentro de Configurações. A guarda de
 * permissão fica com a página, que decide se há algo a mostrar — repeti-la aqui
 * daria duas respostas diferentes para a mesma pergunta.
 *
 * O PAINEL É HERDADO no cadastro: quem entra pela primeira vez encontra uma
 * cópia do painel de quem o cadastrou, em vez de uma tela em branco. Depois
 * disso os dois são independentes. A tela diz isso em voz alta, porque é a
 * pergunta que o admin faz antes de clicar.
 */
/**
 * UM aviso por vez. Cadastrar e remover sao gestos opostos sobre a mesma
 * lista; cada um com o proprio aviso, a tela chegou a dizer "Fulano ja pode
 * entrar" e "Fulano nao tem mais acesso" ao mesmo tempo.
 */
type Aviso = { tipo: "criado" | "removido"; nome: string } | { tipo: "painel"; nomes: string[] } | null;

/** "Maria", "Maria e João", "Maria, João e Ana". */
function listaDeNomes(nomes: string[]): string {
  return new Intl.ListFormat("pt-BR", { style: "long", type: "conjunction" }).format(nomes);
}

export function SecaoPessoas() {
  const { usuario } = useAuth();
  const { state, reload } = useAsync("organizacao/membros", (signal) =>
    listarMembros(signal),
  );
  const [aviso, setAviso] = useState<Aviso>(null);
  const cadastrado = useCallback((nome: string) => {
    setAviso({ tipo: "criado", nome });
    reload();
  }, [reload]);
  const removido = useCallback((nome: string) => {
    setAviso({ tipo: "removido", nome });
    reload();
  }, [reload]);
  const painelEnviado = useCallback((nomes: string[]) => setAviso({ tipo: "painel", nomes }), []);

  return (
    <>
      <FormularioDeCadastro onCadastrado={cadastrado} nomeDeQuemCadastra={usuario?.nome} />
      {aviso && aviso.tipo !== "painel" ? (
        <div className="inline-success" role="status">
          {aviso.tipo === "criado" ? (
            <><b>{aviso.nome}</b> já pode entrar, com o login e a senha que você definiu.</>
          ) : (
            <><b>{aviso.nome}</b> não tem mais acesso.</>
          )}
        </div>
      ) : null}

      {state.status === "error" ? (
        <ErrorState
          title="Não foi possível carregar as pessoas"
          message={state.error instanceof Error ? state.error.message : "Erro inesperado."}
        />
      ) : !hasLoadedOnce(state) ? (
        <LoadingState label="Carregando pessoas…" />
      ) : (
        <>
          <ListaDeMembros membros={state.data ?? []} euId={usuario?.id} onRemovido={removido} />
          <EnviarMeuPainel
            membros={state.data ?? []}
            euId={usuario?.id}
            enviadoPara={aviso?.tipo === "painel" ? aviso.nomes : null}
            onEnviado={painelEnviado}
          />
        </>
      )}
    </>
  );
}

/**
 * Quem tem acesso, e a remoção de cada pessoa.
 *
 * Remover é em DOIS gestos, inline: o primeiro clique abre a confirmação na
 * própria linha, dizendo a consequência (a pessoa perde o acesso agora e o
 * painel pessoal dela nesta empresa é apagado); só o segundo remove. Sem
 * `window.confirm`, que não diz o que vai acontecer e não cabe num teste.
 *
 * A própria linha não tem o botão: o servidor recusa remover a si mesmo, e
 * oferecer o gesto para depois negá-lo seria pior do que não oferecer. O
 * último admin, ao contrário, TEM o botão — a regra depende do resto da lista
 * e quem decide é o servidor; a tela mostra a razão que ele devolve.
 */
function ListaDeMembros({
  membros,
  euId,
  onRemovido,
}: {
  membros: Membro[];
  euId?: string;
  onRemovido: (nome: string) => void;
}) {
  const [confirmando, setConfirmando] = useState<string | null>(null);
  const [removendo, setRemovendo] = useState<string | null>(null);
  const [erro, setErro] = useState<{ userId: string; mensagem: string } | null>(null);

  const remover = useCallback(
    async (membro: Membro) => {
      if (removendo) return;
      setRemovendo(membro.userId);
      setErro(null);
      try {
        await removerMembro(membro.userId);
        setConfirmando(null);
        onRemovido(membro.nome);
      } catch (falha) {
        setErro({ userId: membro.userId, mensagem: mensagemDe(falha, "Não foi possível remover.") });
      } finally {
        setRemovendo(null);
      }
    },
    [onRemovido, removendo],
  );

  return (
    <section>
      <div className="section-h">
        <h2>
          Com acesso <span className="num">{membros.length}</span>
        </h2>
      </div>
      <div className="maint">
        {membros.map((membro) => {
          const souEu = membro.userId === euId;
          const aberto = confirmando === membro.userId;
          return (
            <div className="maint-row maint-row--pessoa" key={membro.userId}>
              <div className="k">
                {membro.nome}
                {souEu ? <span className="badge muted"> você</span> : null}
              </div>
              <div className="v mono">{membro.login}</div>
              <div className="pessoa-acoes">
                <span className={membro.papel === "admin" ? "badge" : "badge muted"}>
                  {membro.papel === "admin" ? "Admin" : "Operador"}
                </span>
                {!souEu && !aberto ? (
                  <button
                    type="button"
                    className="btn ghost sm pessoa-remover"
                    onClick={() => {
                      setErro(null);
                      setConfirmando(membro.userId);
                    }}
                    aria-label={`Remover ${membro.nome}`}
                  >
                    Remover
                  </button>
                ) : null}
              </div>
              {aberto ? (
                <div className="pessoa-confirmacao" role="group" aria-label={`Confirmar remoção de ${membro.nome}`}>
                  <p>
                    <b>{membro.nome}</b> perde o acesso agora mesmo; o painel pessoal dessa
                    pessoa nesta empresa é apagado. Para voltar, precisa ser cadastrada de novo.
                  </p>
                  {erro?.userId === membro.userId ? (
                    <div className="inline-warning" role="alert">
                      {erro.mensagem}
                    </div>
                  ) : null}
                  <div className="pessoa-confirmacao__acoes">
                    <button
                      type="button"
                      className="btn sm pessoa-confirmar"
                      onClick={() => void remover(membro)}
                      disabled={removendo === membro.userId}
                    >
                      {removendo === membro.userId ? "Removendo…" : "Confirmar remoção"}
                    </button>
                    <button
                      type="button"
                      className="btn ghost sm"
                      onClick={() => {
                        setConfirmando(null);
                        setErro(null);
                      }}
                      disabled={removendo === membro.userId}
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </section>
  );
}

/**
 * "Enviar meu painel" (U10): o admin manda o painel dele, por gesto explícito,
 * a quem já existe. É a exceção deliberada ao "independentes depois do
 * cadastro": nada muda sozinho, muda quando o admin manda.
 *
 * Operadores vêm marcados (são para quem o dono pediu); outros admins vêm
 * desmarcados — admin costuma ter painel próprio de propósito. A própria pessoa
 * não aparece: o servidor recusaria, e oferecer para negar é pior.
 *
 * Em DOIS gestos, como remover: a confirmação nomeia quem recebe e diz que o
 * painel atual dessas pessoas é descartado (decisão do dono: sem cópia).
 */
function EnviarMeuPainel({
  membros,
  euId,
  enviadoPara,
  onEnviado,
}: {
  membros: Membro[];
  euId?: string;
  enviadoPara: string[] | null;
  onEnviado: (nomes: string[]) => void;
}) {
  const [escolha, setEscolha] = useState<Record<string, boolean>>({});
  const [confirmando, setConfirmando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const outros = membros.filter((membro) => membro.userId !== euId);
  const marcado = (membro: Membro) => escolha[membro.userId] ?? membro.papel === "operador";
  const selecionados = outros.filter(marcado);

  const enviar = useCallback(async () => {
    if (enviando || selecionados.length === 0) return;
    setEnviando(true);
    setErro(null);
    try {
      await enviarPainel(selecionados.map((membro) => membro.userId));
      setConfirmando(false);
      onEnviado(selecionados.map((membro) => membro.nome));
    } catch (falha) {
      setErro(mensagemDe(falha, "Não foi possível enviar o painel."));
    } finally {
      setEnviando(false);
    }
  }, [enviando, onEnviado, selecionados]);

  if (outros.length === 0) return null;
  const n = selecionados.length;
  return (
    <section aria-labelledby="enviar-painel-titulo" className="envio-painel">
      <div className="section-h">
        <h2 id="enviar-painel-titulo">Enviar meu painel</h2>
      </div>
      <p className="muted envio-painel__texto">
        Substitui o painel das pessoas marcadas por uma cópia do seu, em todos os setores: cards,
        nomes e unidades dos sinais, janela dos gráficos e indicadores de turno exibidos. O painel
        atual delas é descartado.
      </p>
      <fieldset className="envio-painel__pessoas" disabled={enviando}>
        <legend className="sr-only">Quem recebe o seu painel</legend>
        {outros.map((membro) => (
          <label key={membro.userId} className="envio-painel__pessoa">
            <input
              type="checkbox"
              checked={marcado(membro)}
              onChange={(e) => {
                setErro(null);
                setEscolha((atual) => ({ ...atual, [membro.userId]: e.target.checked }));
              }}
            />
            <span>{membro.nome}</span>
            <span className={membro.papel === "admin" ? "badge" : "badge muted"}>
              {membro.papel === "admin" ? "Admin" : "Operador"}
            </span>
          </label>
        ))}
      </fieldset>

      {enviadoPara && !confirmando ? (
        <div className="inline-success" role="status">
          Painel enviado para <b>{listaDeNomes(enviadoPara)}</b>. Quem estiver com a tela aberta
          recebe em até 5 minutos um aviso para carregar o painel novo; tela parada carrega sozinha.
        </div>
      ) : null}

      {confirmando ? (
        <div className="pessoa-confirmacao" role="group" aria-label="Confirmar envio do painel">
          <p>
            O painel de <b>{listaDeNomes(selecionados.map((membro) => membro.nome))}</b> passa a ser
            uma cópia do seu agora, em todos os setores. O que {n === 1 ? "essa pessoa tinha" : "essas pessoas tinham"} ajustado
            é descartado e não há como desfazer.
          </p>
          {erro ? (
            <div className="inline-warning" role="alert">
              {erro}
            </div>
          ) : null}
          <div className="pessoa-confirmacao__acoes">
            <button
              type="button"
              className="btn sm pessoa-confirmar"
              onClick={() => void enviar()}
              disabled={enviando || n === 0}
            >
              {enviando ? "Enviando…" : "Confirmar envio"}
            </button>
            <button
              type="button"
              className="btn ghost sm"
              onClick={() => {
                setConfirmando(false);
                setErro(null);
              }}
              disabled={enviando}
            >
              Cancelar
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          className="btn primary"
          disabled={n === 0}
          onClick={() => {
            setErro(null);
            setConfirmando(true);
          }}
        >
          {n === 0 ? "Enviar meu painel" : `Enviar meu painel para ${n} ${n === 1 ? "pessoa" : "pessoas"}`}
        </button>
      )}
    </section>
  );
}

function FormularioDeCadastro({
  onCadastrado,
  nomeDeQuemCadastra,
}: {
  onCadastrado: (nome: string) => void;
  nomeDeQuemCadastra?: string;
}) {
  const [nome, setNome] = useState("");
  const [login, setLogin] = useState("");
  const [senha, setSenha] = useState("");
  const [papel, setPapel] = useState<Papel>("operador");
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const enviar = useCallback(
    async (evento: FormEvent) => {
      evento.preventDefault();
      if (enviando) return;
      setEnviando(true);
      setErro(null);
      try {
        const membro = await cadastrarMembro({
          nome: nome.trim(),
          login: login.trim(),
          senha,
          papel,
        });
        setNome("");
        setLogin("");
        setSenha("");
        setPapel("operador");
        onCadastrado(membro.nome);
      } catch (falha) {
        setErro(mensagemDe(falha, "Não foi possível cadastrar."));
      } finally {
        setEnviando(false);
      }
    },
    [enviando, login, nome, onCadastrado, papel, senha],
  );

  return (
    <section>
      <div className="section-h">
        <h2>Cadastrar pessoa</h2>
      </div>

      <form className="form-grid form-grid--2" onSubmit={enviar}>
        <label className="field">
          <span className="field__label">Nome</span>
          <input
            className="input"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            required
          />
        </label>

        <label className="field">
          <span className="field__label">Login</span>
          <input
            className="input mono"
            value={login}
            onChange={(e) => setLogin(e.target.value)}
            placeholder="nome@empresa.com.br"
            required
          />
          <span className="field__hint">
            Único no sistema inteiro — a tela de entrada não pergunta a empresa.
          </span>
        </label>

        <label className="field">
          <span className="field__label">Senha</span>
          <input
            className="input"
            type="password"
            value={senha}
            onChange={(e) => setSenha(e.target.value)}
            autoComplete="new-password"
            required
          />
          <span className="field__hint">
            Mínimo de 6 caracteres. Você a entrega à pessoa; ela não recebe por e-mail.
          </span>
        </label>

        <label className="field">
          <span className="field__label">Papel</span>
          <select
            className="input"
            value={papel}
            onChange={(e) => setPapel(e.target.value === "admin" ? "admin" : "operador")}
          >
            <option value="operador">Operador — lê o painel</option>
            <option value="admin">Admin — configura e cadastra pessoas</option>
          </select>
        </label>

        <p className="muted" style={{ gridColumn: "1 / -1", fontSize: 12.5, margin: 0 }}>
          A pessoa vai encontrar uma <b>cópia do seu painel</b>
          {nomeDeQuemCadastra ? `, ${nomeDeQuemCadastra}` : ""} — assim ela não começa de
          uma tela em branco. A partir daí os dois são independentes: ela ajusta o painel
          dela sem mexer no seu, e você no seu sem mexer no dela. Para mandar mudanças
          suas depois, use <b>Enviar meu painel</b>, mais abaixo.
        </p>

        {erro ? (
          <div className="inline-warning" role="alert" style={{ gridColumn: "1 / -1" }}>
            {erro}
          </div>
        ) : null}

        <div style={{ gridColumn: "1 / -1" }}>
          <button className="btn primary" type="submit" disabled={enviando}>
            {enviando ? "Cadastrando…" : "Cadastrar pessoa"}
          </button>
        </div>
      </form>
    </section>
  );
}
